import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { streamAI } from "@/lib/ai";
import { usePatientAuth } from "@/context/PatientAuthContext";
import { Button } from "@/components/ui/button";
import { MrdManager, CaseCard } from "@/components/opd/PatientRecordDialog";
import { ChatBox } from "@/components/opd/PatientChat";
import { HeartPulse, LogOut, Bell, CalendarDays, CalendarClock, CheckCheck, Loader2, Stethoscope, FolderOpen, MessageCircle, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

const ICON = { appointment: CalendarDays, follow_up: CalendarClock, reminder: Bell, general: Bell };

function PortalAiAssistant() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  useEffect(() => {
    if (msgs.length === 0) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const q = input.trim();
    if (!q || streaming) return;
    const history = msgs.map((m) => ({ role: m.role, text: m.text }));
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setInput(""); setStreaming(true);
    try {
      await streamAI("/portal/ai/assistant", {
        portal: true,
        body: { message: q, history },
        onChunk: (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + t }; return c; }),
      });
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Sorry, I couldn't answer just now. Please try again." }; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <section className="bg-white border border-indigo-200 rounded-xl p-5 mt-6" data-testid="portal-ai-assistant">
      <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-indigo-600" /> Ask Mediva</h2>
      <p className="text-sm text-slate-500 mb-2">Get plain-language help with your prescriptions, follow-ups and general wellness questions.</p>
      <div className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2 mb-3" data-testid="ai-assistant-disclaimer">
        This assistant gives general information about your own records and is not a substitute for professional medical advice. For emergencies, contact your doctor or local emergency services.
      </div>
      <div className="flex flex-col h-[46vh]">
        <div className="flex-1 overflow-y-auto thin-scroll space-y-2 p-1" ref={listRef}>
          {msgs.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Ask me anything about your prescriptions, follow-up dates, or how to prepare for a visit.</div>}
          {msgs.map((m, i) => {
            const mine = m.role === "user";
            return (
              <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${mine ? "bg-teal-600 text-white rounded-br-sm" : "bg-indigo-50 text-slate-800 rounded-bl-sm border border-indigo-100"}`} data-testid={`ai-msg-${i}`}>
                  {!mine && <div className="text-[11px] font-medium text-indigo-600 mb-0.5 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Mediva</div>}
                  <div className="text-sm whitespace-pre-wrap break-words">{m.text}{!mine && streaming && i === msgs.length - 1 && <span className="inline-block w-2 h-4 bg-indigo-400 ml-0.5 animate-pulse align-middle" />}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <input className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), send())} placeholder="Ask Mediva a question…" data-testid="ai-assistant-input" />
          <Button size="icon" onClick={send} disabled={streaming || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="ai-assistant-send">{streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </div>
      </div>
    </section>
  );
}

export default function PortalDashboard() {
  const { patient, logout } = usePatientAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cases, setCases] = useState(null);
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  const loadDocs = () => api.get("/portal/mrd").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => {
    load();
    api.get("/portal/cases").then((r) => setCases(r.data)).catch(() => setCases([]));
    loadDocs();
  }, []);

  const uploadDoc = async ({ file, category, title }) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", category); if (title) fd.append("title", title);
      await api.post("/portal/mrd", fd);
      toast.success("Document uploaded to your records");
      loadDocs();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };
  const deleteDoc = async (d) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/portal/mrd/${d.id}`); toast.success("Deleted"); loadDocs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };
  const viewDoc = async (d) => {
    try { const res = await api.get(`/portal/mrd/${d.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank", "noopener,noreferrer"); }
    catch { toast.error("Unable to open document"); }
  };

  const [pmsgs, setPmsgs] = useState([]);
  const [pmsgsLoading, setPmsgsLoading] = useState(true);
  const [psending, setPsending] = useState(false);
  const [punread, setPunread] = useState(0);
  const loadMsgs = () => api.get("/portal/messages").then((r) => setPmsgs(r.data || [])).catch(() => {}).finally(() => setPmsgsLoading(false));
  const loadUnread = () => api.get("/portal/messages/unread").then((r) => setPunread(r.data?.count || 0)).catch(() => {});
  useEffect(() => {
    loadMsgs(); loadUnread();
    const iv = setInterval(() => { loadMsgs(); loadUnread(); }, 5000);
    return () => clearInterval(iv);
  }, []);
  const markChatRead = () => { if (punread > 0) { api.post("/portal/messages/read").then(() => setPunread(0)).catch(() => {}); } };
  const sendMsg = async (text) => {
    setPsending(true);
    try { await api.post("/portal/messages", { text }); await loadMsgs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setPsending(false); }
  };

  const markRead = async (id) => { await api.post(`/portal/notifications/${id}/read`); load(); };
  const markAll = async () => { await api.post("/portal/notifications/read-all"); load(); };
  const doLogout = async () => { await logout(); navigate("/portal/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center px-5 sm:px-8 sticky top-0 z-20">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-lg bg-teal-600 grid place-items-center text-white"><HeartPulse className="h-4 w-4" /></span>
          <span className="font-display font-semibold text-slate-900">SPAN Patient Portal</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-slate-600 hidden sm:block">{patient?.name}</span>
          <Button variant="outline" size="sm" onClick={doLogout} data-testid="portal-logout"><LogOut className="h-4 w-4 mr-1.5" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-5 sm:p-8">
        {data === null ? <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div> :
         data === false ? <div className="text-slate-400 py-20 text-center">Unable to load your portal.</div> : (
          <>
            <div className="mb-6">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Hello, {patient?.name?.split(" ")[0]}</h1>
              <p className="text-sm text-slate-500 mt-1 font-mono">UHID {patient?.uhid}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="portal-appointments">
                <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-3"><CalendarDays className="h-4 w-4 text-teal-600" /> Upcoming appointments</h2>
                <div className="space-y-2">
                  {data.appointments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-4 py-3 bg-teal-50/40">
                      <div><div className="font-medium text-slate-800">{a.date} · {a.start_time}</div><div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5"><Stethoscope className="h-3.5 w-3.5" />{a.doctor_name || "Doctor"} · {a.reason || "Consultation"}</div></div>
                      <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 capitalize">{a.status}</span>
                    </div>
                  ))}
                  {data.appointments.length === 0 && <div className="text-sm text-slate-400">No upcoming appointments.</div>}
                </div>
                {data.followups.length > 0 && (
                  <div className="mt-5">
                    <h3 className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Follow-ups advised</h3>
                    {data.followups.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-700 py-1"><CalendarClock className="h-4 w-4 text-amber-500" />{f.follow_up_date}{f.follow_up_advice ? ` — ${f.follow_up_advice}` : ""}</div>
                    ))}
                  </div>
                )}
              </section>

              <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="portal-notifications">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><Bell className="h-4 w-4 text-teal-600" /> Notifications {data.unread > 0 && <span className="text-xs bg-teal-600 text-white rounded-full px-2 py-0.5" data-testid="portal-unread-badge">{data.unread}</span>}</h2>
                  {data.unread > 0 && <Button variant="ghost" size="sm" onClick={markAll} data-testid="mark-all-read"><CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read</Button>}
                </div>
                <div className="space-y-2">
                  {data.notifications.map((n) => {
                    const Ic = ICON[n.type] || Bell;
                    return (
                      <button key={n.id} onClick={() => !n.read && markRead(n.id)} data-testid={`notif-${n.id}`}
                        className={`w-full text-left flex gap-3 rounded-lg px-4 py-3 border transition-colors ${n.read ? "border-slate-100 bg-white" : "border-teal-200 bg-teal-50/60 hover:bg-teal-50"}`}>
                        <span className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${n.read ? "bg-slate-100 text-slate-400" : "bg-teal-100 text-teal-700"}`}><Ic className="h-4 w-4" /></span>
                        <div className="min-w-0"><div className="text-sm font-medium text-slate-800 flex items-center gap-2">{n.title}{!n.read && <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />}</div><div className="text-xs text-slate-500 mt-0.5">{n.body}</div><div className="text-[10px] text-slate-400 font-mono mt-1">{new Date(n.created_at).toLocaleString()}</div></div>
                      </button>
                    );
                  })}
                  {data.notifications.length === 0 && <div className="text-sm text-slate-400">No notifications yet.</div>}
                </div>
              </section>
            </div>

            <section className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="portal-prescriptions">
              <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-3"><Stethoscope className="h-4 w-4 text-teal-600" /> My prescriptions</h2>
              {cases === null ? <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-teal-600" /></div> : (
                <div className="space-y-2">
                  {cases.map((c) => <CaseCard key={c.id} c={c} patient={patient} />)}
                  {cases.length === 0 && <div className="text-sm text-slate-400">No prescriptions yet. They appear here after a doctor completes your consultation.</div>}
                </div>
              )}
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="portal-mrd">
              <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-1"><FolderOpen className="h-4 w-4 text-teal-600" /> Medical records briefcase</h2>
              <p className="text-sm text-slate-500 mb-4">Your lifetime store of reports and hospitalisation records. Upload your own documents anytime.</p>
              <MrdManager docs={docs} loading={docs === null} uploading={uploading}
                canUpload={true} canDeleteDoc={(d) => d.uploader_type === "patient"}
                onUpload={uploadDoc} onDelete={deleteDoc} onView={viewDoc} />
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="portal-chat">
              <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-1"><MessageCircle className="h-4 w-4 text-teal-600" /> Chat with your care team
                {punread > 0 && <span className="inline-flex items-center text-[11px] font-semibold bg-teal-600 text-white rounded-full px-2 py-0.5" data-testid="portal-chat-unread">{punread} new</span>}
              </h2>
              <p className="text-sm text-slate-500 mb-3">Ask routine questions — your doctor and care team will reply here.</p>
              <ChatBox messages={pmsgs} onSend={sendMsg} loading={pmsgsLoading} sending={psending} onOpen={markChatRead}
                isMine={(m) => m.sender_type === "patient"} emptyText="No messages yet. Say hello to your care team." placeholder="Message your care team" />
            </section>

            <PortalAiAssistant />
          </>
        )}
      </main>
    </div>
  );
}
